import React from "react";
import { Divider, Typography } from "@mui/material";
import { TextField } from "@outpave/ds";
import SettingsRow from "./SettingsRow";
import { useTranslation } from "react-i18next";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { CompanyProfileTabProps } from "hooks/useCompanySettings";
import { FormikProps } from "formik";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import { useFeatureEntitlements } from "@frontegg/react";
import { BUSINESS_CARD_NAME_MAX_LENGTH } from "validations/companySettings";

interface ProfileSectionProps {
  editMode: boolean;
  profileFormik: FormikProps<CompanyProfileTabProps>;
  uploading: boolean;
  paymentEmailDomain?: string;
}

const formatURL = (url: string) => {
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return `http://${url}`;
  }
  return url;
};

const profileFields = [
  {
    label: "Legal Name",
    description: "BusinessLegalNameDescription",
    field: "businessLegalName",
  },
  {
    label: "Doing Business As (DBA)",
    description: "BusinessDBADescription",
    field: "businessDBA",
  },
  {
    label: "Business Card Name",
    description: "BusinessCardNameDescription",
    field: "businessCardName",
    maxLength: BUSINESS_CARD_NAME_MAX_LENGTH,
  },
  {
    label: "Description",
    description: "BusinessGeneralDescription",
    field: "description",
  },
  {
    label: "Email",
    description: "BusinessEmailDescription",
    field: "businessEmail",
  },
  {
    label: "Payment Email",
    description: "PaymentEmailDescription",
    field: "paymentEmail",
    isPaymentEmail: true,
  },
  {
    label: "Phone",
    description: "BusinessPhoneDescription",
    field: "phoneNumber",
  },
  {
    label: "Website",
    description: "BusinessWebsiteDescription",
    field: "websiteURL",
  },
];

/**
 * The rows this tab renders, each entry saying whether that row has a
 * description, so a loading skeleton can match what replaces it.
 */
export const profileSkeletonRows = (hasPaymentsFeature: boolean) =>
  profileFields
    .filter((field) => hasPaymentsFeature || !("isPaymentEmail" in field))
    .map((field) => Boolean(field.description));

const ProfileSettings: React.FC<ProfileSectionProps> = ({
  editMode,
  profileFormik,
  uploading,
  paymentEmailDomain,
}) => {
  const { t } = useTranslation();
  const { handleFormikHelperText, handleFormikError } =
    useFormikHelpers(profileFormik);
  const { isEntitled: featurePayments } =
    useFeatureEntitlements("feature_payments");

  const renderFieldValue = (
    field: string,
    label: string,
    isPaymentEmail?: boolean,
    maxLength?: number,
  ) => {
    const fieldValue = String(profileFormik.values[field] ?? "");

    // Edit mode rendering
    if (editMode) {
      return (
        <div
          style={{ display: "flex", alignItems: "flex-start", width: "100%" }}
        >
          <TextField
            fullWidth
            size="md"
            // The row title is a heading beside the field, not a label bound to
            // it, so the control carries its own name. The payment email's
            // domain is fixed text outside the input, so it is named too, or
            // the address is announced with its second half missing.
            aria-label={
              isPaymentEmail && paymentEmailDomain
                ? `${label} ${paymentEmailDomain}`
                : label
            }
            value={fieldValue}
            onChange={(e) => profileFormik.setFieldValue(field, e.target.value)}
            // Not waiting for a touch: a stored value can already be invalid,
            // and that disables Save on a field the reader never opened.
            error={handleFormikError(field, { ignoreTouched: true })}
            // The DS treats any falsy errorText as "no message", so null must become
            // undefined or the helper slot silently renders nothing at all.
            errorText={
              handleFormikHelperText(field, { ignoreTouched: true }) ??
              undefined
            }
            onBlur={profileFormik.handleBlur(field)}
            inputProps={maxLength ? { maxLength } : undefined}
          />

          {isPaymentEmail && paymentEmailDomain && (
            <Typography
              variant="body1"
              style={{
                marginLeft: 8,
                whiteSpace: "nowrap",
                marginTop: "7px",
              }}
            >
              {paymentEmailDomain}
            </Typography>
          )}
        </div>
      );
    }

    // Display mode rendering
    if (isPaymentEmail) {
      return (
        <Typography variant="body1">
          {`${fieldValue}${paymentEmailDomain}`}
        </Typography>
      );
    }

    if (field === "websiteURL") {
      return (
        <Typography
          variant="body1"
          sx={{ color: "var(--color-text-link)", textDecoration: "underline" }}
        >
          <a
            href={formatURL(fieldValue)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {fieldValue}
          </a>
        </Typography>
      );
    }

    return <Typography variant="body1">{fieldValue}</Typography>;
  };

  return (
    <>
      {profileFields.map(
        ({ label, description, field, isPaymentEmail, maxLength }, index) => {
          if (isPaymentEmail && !featurePayments) return null;
          return (
            // The key belongs on the list item's root, which is this fragment.
            <React.Fragment key={field}>
              <SettingsRow
                label={t(label)}
                description={description ? t(description) : undefined}
              >
                {renderFieldValue(field, t(label), isPaymentEmail, maxLength)}
              </SettingsRow>
              {index < profileFields.length - 1 && <Divider />}
            </React.Fragment>
          );
        },
      )}

      <LoadingWithoutModal open={uploading} />
    </>
  );
};

export default ProfileSettings;
